import { useState } from "react";
import { Phone, ChevronRight, CircleCheck as CheckCircle2, ShieldCheck, Star, ArrowRight, Ruler, Loader } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1440'%3E%3Crect fill='%23081438' width='1920' height='1440'/%3E%3C/svg%3E";
const HERO_IMG = {
  desktop: "artifact_img_ddnypb1g",
  tablet: "artifact_img_ddnypb1g",
  mobile: "artifact_img_ddnypb1g"
};
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
export default function InstallationHero() {
  const formFooterItems = [{
    label: "Licensed",
    icon: ShieldCheck
  }, {
    label: "Insured",
    icon: CheckCircle2
  }, {
    label: "5.0 Rated",
    icon: Star
  }];
  return <section className="relative isolate overflow-hidden !bg-[url(artifact_img_ddnypb1g)] !bg-[url(https://goldgaragedoor.com.au/wp-content/uploads/2026/06/repair-hero-desktop-1.webp)]" style={{
    backgroundColor: NAVY
  }} aria-label="Garage Door Installation Sydney">
      <picture className="absolute inset-0 -z-10">
        <source media="(max-width: 767px)" srcSet={HERO_IMG.mobile} type="image/webp" />
        <source media="(max-width: 1279px)" srcSet={HERO_IMG.tablet} type="image/webp" />
        <img src={HERO_IMG.desktop} data-wvc-srcset={`${HERO_IMG.desktop}-wvc-srcset`} data-wvc-sizes={`${HERO_IMG.desktop}-wvc-sizes`} alt="Modern Sydney home with premium Gold Garage Door sectional garage door installation" className="h-full w-full object-cover" style={{
        objectPosition: "65% center"
      }} loading="eager"
      // @ts-ignore
      fetchpriority="high" decoding="async" width={1920} height={1440} />
      </picture>

      <div className="absolute inset-0 -z-10" style={{
      background: `linear-gradient(90deg, ${NAVY}F2 0%, ${NAVY}E6 40%, ${NAVY}B3 70%, ${NAVY}80 100%)`
    }} />
      <div className="absolute inset-0 -z-10" style={{
      background: `radial-gradient(ellipse at 20% 50%, ${NAVY_LIGHT}66 0%, transparent 50%), linear-gradient(180deg, ${NAVY}40 0%, transparent 30%, transparent 70%, ${NAVY} 100%)`
    }} />

      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-24 lg:pb-28">
        {}
        <nav aria-label="Breadcrumb" className="mb-10 flex items-center gap-2 text-[13px]" style={{
        fontFamily: FONT_BODY,
        color: "rgba(255,255,255,0.6)"
      }}>
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-50" />
          <Link to="#" className="hover:text-white transition-colors">
            Services
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-50" />
          <span style={{
          color: "rgba(255,255,255,0.95)"
        }}>New Door Installation</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

          {}
          <div className="lg:col-span-7">
            {}
            <div className="mb-6 inline-flex items-center gap-2.5 rounded-lg border px-4 py-2" style={{
            backgroundColor: "rgba(245,166,35,0.10)",
            borderColor: "rgba(245,166,35,0.30)"
          }}>
              <Ruler className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
              <span className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{
              fontFamily: FONT_BODY,
              color: GOLD
            }}>
                Made-to-Measure · Sydney Wide
              </span>
            </div>

            {}
            <h1 className="font-extrabold text-white" style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(2.25rem, 5vw, 4.25rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.025em"
          }}>
              A new garage door &mdash;{" "}
              <span style={{
              color: GOLD
            }}>sized, built, installed</span> by family.
            </h1>

            {}
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed" style={{
            fontFamily: FONT_BODY,
            color: "rgba(255,255,255,0.82)"
          }}>
              Sectional, roller, tilt, custom &mdash; every major brand,
              made-to-measure for your driveway and your home.{" "}
              <span className="text-white font-semibold">
                On-site measure, fixed-price quote, one-day install.
              </span>{" "}
              Family-run since 2009, fully insured.
            </p>

            {}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
              color: "rgba(255,255,255,0.5)"
            }}>
                Brands we install —
              </span>
              {["B&D", "Steel-Line", "Gliderol", "Centurion", "4D Industrial", "Hörmann"].map((brand, i) => <span key={brand} data-index={i} className="text-[11px] font-bold tracking-wide rounded-lg px-2.5 py-1 border" style={{
              borderColor: "rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.85)",
              fontFamily: FONT_BODY
            }}>
                  {brand}
                </span>)}
            </div>

            {}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <Link to="tel:0485000933" aria-label="Call Gold Garage Door on 0485 000 933" className="group inline-flex items-center justify-center gap-3 rounded-lg px-7 py-[14px] text-[15px] font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_BODY,
              boxShadow: `0 14px 40px -10px ${GOLD}99`
            }}>
                <Phone className="h-[18px] w-[18px]" strokeWidth={2.5} />
                Call 0485 000 933
              </Link>
              <Link to="https://wa.me/61485000933" className="group inline-flex items-center justify-center gap-2 rounded-lg border-2 px-7 py-3 text-[15px] font-semibold text-white transition-all duration-200" style={{
              borderColor: "rgba(255,255,255,0.35)",
              fontFamily: FONT_BODY
            }}>
                WhatsApp Us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3" style={{
            fontFamily: FONT_BODY
          }}>
              {[{
              icon: CheckCircle2,
              label: "On-Site Measure"
            }, {
              icon: ShieldCheck,
              label: "Workmanship Warranty"
            }, {
              icon: Star,
              label: "5.0 Google Rated"
            }].map(({
              icon: Icon,
              label
            }, i) => <li key={label} data-index={i} className="flex items-center gap-2.5">
                  <Icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} style={{
                color: GOLD
              } as React.CSSProperties} />
                  <span className="text-sm font-medium" style={{
                color: "rgba(255,255,255,0.92)"
              }}>
                    {label}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div id="contact-us" className="lg:col-span-5 relative bg-white backdrop-blur-sm p-6 md:p-8 rounded-lg border border-[#F5A623]/40 shadow-2xl shadow-[#081438]/10 overflow-hidden scroll-mt-28">
            <div className="absolute inset-0 opacity-[0.015] pointer-events-none rounded-lg" style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
          }} />
            
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#F5A623]/0 via-[#F5A623] to-[#F5A623]/0" />

            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase mb-2" style={{
              fontFamily: FONT_BODY,
              color: GOLD
            }}>
                <Ruler className="h-3.5 w-3.5" strokeWidth={2.5} />
                Installation Quote · 60 seconds
              </div>
              <h2 className="text-xl font-bold text-[#081438]" style={{
              fontFamily: FONT_HEAD,
              letterSpacing: "-0.015em"
            }}>
                Get Your Free Quote
              </h2>
              <p className="mt-2 text-[13.5px] text-[#555] font-medium leading-relaxed" style={{
              fontFamily: FONT_BODY
            }}>
                Tell us about your project &mdash; we&rsquo;ll measure on-site and quote properly.
              </p>
              <div className="h-px bg-gradient-to-r from-transparent via-[#F5A623]/20 to-transparent my-4"></div>
            </div>

            <FormProvider formId="hero_quote_form" sectionName="Hero Section" defaultValues={{
            name: "",
            phone: "",
            suburb: "",
            service: "Replace Existing Door"
          }}>
              <HeroQuoteForm />
            </FormProvider>

            <div className="mt-6 pt-4 border-t border-black/5 flex justify-center gap-5">
              {formFooterItems.map((item, i) => <div key={item.label} data-index={i} className="flex items-center gap-1.5">
                  <item.icon className="h-3.5 w-3.5" style={{
                color: GOLD
              }} />
                  <span className="text-[12px] font-semibold text-[#333] uppercase tracking-wider">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}80 50%, transparent 100%)`
    }} />
    </section>;
}
function HeroQuoteForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage,
    submitError
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center" style={{
      fontFamily: FONT_BODY
    }}>
        <CheckCircle2 className="h-12 w-12 mb-3" style={{
        color: GOLD
      }} />
        <div className="text-[#081438] font-semibold text-lg">
          {successMessage || "Quote Request Sent!"}
        </div>
        <p className="mt-1 text-sm text-[#555]">
          We&rsquo;ll be in touch shortly to arrange your measure.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3.5">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-semibold uppercase tracking-wider text-[#555]">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="bg-black/5 border-black/10 text-[#081438] placeholder:text-black/30 focus:border-[#F5A623] focus:ring-[#F5A623]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-2 gap-3">
        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] font-semibold uppercase tracking-wider text-[#555]">Phone</FormLabel>
              <FormControl>
                <Input placeholder="04xx xxx xxx" {...field} className="bg-black/5 border-black/10 text-[#081438] placeholder:text-black/30 focus:border-[#F5A623] focus:ring-[#F5A623]/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="suburb" rules={{
        required: "Suburb is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] font-semibold uppercase tracking-wider text-[#555]">Suburb</FormLabel>
              <FormControl>
                <Input placeholder="Bondi" {...field} className="bg-black/5 border-black/10 text-[#081438] placeholder:text-black/30 focus:border-[#F5A623] focus:ring-[#F5A623]/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-semibold uppercase tracking-wider text-[#555]">Project Type</FormLabel>
            <FormControl>
              <select {...field} className="w-full rounded-lg border border-black/10 bg-black/5 px-4 py-2.5 text-[15px] text-[#081438] outline-none transition-all appearance-none cursor-pointer focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23F5A623' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 14px center",
          paddingRight: "40px"
        }}>
                {["Replace Existing Door", "New Build / First Install", "Renovation / Extension", "Heritage / Match Existing", "Custom Size / Non-Standard", "Multiple Doors", "Other / Need Advice"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-xs font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-2 h-12 rounded-lg bg-[#F5A623] hover:bg-[#F5A623]/90 text-[#081438] font-bold shadow-lg shadow-[#F5A623]/20 transition-all hover:scale-[1.01] active:scale-[0.99]">
        {isSubmitting ? <Loader className="h-5 w-5 animate-spin" /> : <>
            Request My Free Quote
            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
          </>}
      </Button>

      <p className="text-[11px] text-center mt-2 text-[#555]/60 font-medium">
        No spam · We respond within 24h during business days
      </p>
    </Form>;
}